import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

import { OpenAPI } from "../../../client"
import { legacyEnforcementLinesForProduct } from "../../../utils/licenseBootstrapSnippet.mjs"

export const Route = createFileRoute("/_layout/admin/licenses")({
  component: LicensesAdminPage,
})

type LicenseItem = {
  id: number
  customer_id: string
  customer_code: string
  license_id: string
  product: RuntimeTarget
  plan: string
  status: string
  starts_at: string
  expires_at: string
  grace_days: number
  offline_max_days: number
  critical_after_limited_days: number
  capability_enforcement_mode: "enforce" | "audit" | string
  emergency_finalize_enabled: boolean
  license_revision: number
  contract_version: number
  module_overrides: Record<string, boolean>
  features: Record<string, boolean>
  limits: Record<string, number>
  updated_at: string
}
type CustomerItem = {
  customer_id: string
  code: string
  name: string
  status: string
}
type CustomerListResponse = {
  items: CustomerItem[]
  total: number
}

type LicenseListResponse = {
  items: LicenseItem[]
  total: number
  limit: number
  offset: number
}

type LicenseMetricsResponse = {
  license: LicenseItem
  installations: InstallationCredentialItem[]
  usage_today: Array<{ metric_key: string; count: number; usage_date: string }>
}

type IssuedTokenItem = {
  id: number
  jti: string
  kid: string
  alg: string
  issued_at: string
  expires_at: string
}

type LicenseDetailResponse = {
  license: LicenseItem
  issued_tokens: IssuedTokenItem[]
  installations: InstallationCredentialItem[]
}

type InstallationCredentialItem = {
  id: string
  customer_id: string
  license_id: string
  installation_id: string
  product: RuntimeTarget
  environment: string
  status: string
  last_used_at?: string | null
  rotated_at?: string | null
  created_at: string
  updated_at: string
}

type InstallationListResponse = {
  items: InstallationCredentialItem[]
}

type InstallationSecretsResponse = {
  credential: InstallationCredentialItem
  secrets: {
    runtime_shared_key: string
    webhook_shared_key: string
  }
}

type RuntimeTarget = "wms" | "hrm"

type FeatureDefinition = {
  key: string
  label: string
  description?: string
  basic_included?: boolean
  pro_included?: boolean
  dependencies?: string[]
  editable?: boolean
}

type LimitDefinition = {
  key: string
  label: string
  description?: string
  default_value: number
  min_value: number
}

type ProductContract = {
  product: RuntimeTarget
  contract_version?: number
  plans?: Record<string, { label: string; included: Record<string, boolean> }>
  modules?: Array<{
    key: string
    label: string
    scope: string
    dependencies: string[]
    editable: boolean
  }>
  default_features: Record<string, boolean>
  default_limits: Record<string, number>
  feature_definitions: FeatureDefinition[]
  limit_definitions: LimitDefinition[]
}

type LicenseOptionsResponse = {
  statuses: string[]
  products: Record<RuntimeTarget, ProductContract>
  bootstrap: {
    issuer_default: string
    audiences: Record<RuntimeTarget, string>
  }
}

type UpsertPayload = {
  customer_id: string
  customer_code: string
  license_id: string
  product: RuntimeTarget
  plan: string
  status: string
  starts_at: string
  expires_at: string
  grace_days: number
  offline_max_days: number
  critical_after_limited_days: number
  capability_enforcement_mode: "enforce" | "audit" | string
  emergency_finalize_enabled: boolean
  contract_version?: number
  module_overrides?: Record<string, boolean>
  expected_revision?: number
  features: Record<string, boolean>
  limits: Record<string, number>
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("access_token") || ""
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      const message =
        parsed?.error ||
        parsed?.detail ||
        parsed?.message ||
        `HTTP ${res.status}`
      throw new Error(String(message))
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  return (await res.json()) as T
}

function getLicenseStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "expired_grace":
      return "yellow"
    case "expired_limited":
    case "suspended":
      return "orange"
    case "expired_critical":
    case "unlicensed":
    case "revoked":
      return "red"
    default:
      return "gray"
  }
}

function cloneBooleanMap(input?: Record<string, boolean>) {
  return { ...(input ?? {}) }
}

function cloneNumberMap(input?: Record<string, number>) {
  return { ...(input ?? {}) }
}

function toDateInputValue(value?: string) {
  if (!value) return ""
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ""
  return date.toISOString().slice(0, 10)
}

function buildUpsertPayloadFromLicense(item: LicenseItem): UpsertPayload {
  return {
    customer_id: item.customer_id,
    customer_code: item.customer_code || "",
    license_id: item.license_id,
    product: item.product,
    plan: item.plan || "PRO",
    status: item.status || "active",
    starts_at: toDateInputValue(item.starts_at),
    expires_at: toDateInputValue(item.expires_at),
    grace_days: Number.isFinite(item.grace_days) ? item.grace_days : 14,
    offline_max_days: Number.isFinite(item.offline_max_days)
      ? item.offline_max_days
      : 14,
    critical_after_limited_days: Number.isFinite(
      item.critical_after_limited_days,
    )
      ? item.critical_after_limited_days
      : 30,
    capability_enforcement_mode:
      item.capability_enforcement_mode === "audit" ? "audit" : "enforce",
    emergency_finalize_enabled: Boolean(item.emergency_finalize_enabled),
    contract_version: item.contract_version ?? 1,
    module_overrides: cloneBooleanMap(item.module_overrides),
    expected_revision: item.license_revision,
    features: cloneBooleanMap(item.features),
    limits: cloneNumberMap(item.limits),
  }
}

type FeatureGroup = {
  key: string
  title: string
  features: FeatureDefinition[]
}

function normalizeProductContract(
  product: RuntimeTarget,
  contract?: Partial<ProductContract> | null,
): ProductContract {
  return {
    product,
    contract_version: contract?.contract_version,
    plans: contract?.plans,
    modules: contract?.modules,
    default_features: cloneBooleanMap(contract?.default_features),
    default_limits: cloneNumberMap(contract?.default_limits),
    feature_definitions: Array.isArray(contract?.feature_definitions)
      ? contract.feature_definitions
      : [],
    limit_definitions: Array.isArray(contract?.limit_definitions)
      ? contract.limit_definitions
      : [],
  }
}

function serializeLicensePayload(
  payload: UpsertPayload,
  contract?: ProductContract,
) {
  if (payload.product !== "wms") return payload
  if (!contract?.modules?.length || !contract.plans?.[payload.plan]) {
    throw new Error(
      "Nie udało się wczytać aktualnego kontraktu modułów. Odśwież opcje licencji.",
    )
  }
  const moduleOverrides: Record<string, boolean> = {}
  for (const module of contract.modules) {
    if (payload.module_overrides?.[module.key] === false)
      moduleOverrides[module.key] = false
  }
  const { features: _legacyFeatures, ...base } = payload
  return {
    ...base,
    contract_version: contract.contract_version ?? 2,
    module_overrides: moduleOverrides,
  }
}

function buildFeatureGroups(
  product: RuntimeTarget,
  features: FeatureDefinition[],
): FeatureGroup[] {
  if (product === "wms") {
    return [
      {
        key: "warehouse_operations",
        title: "Складські операції",
        features: features.filter((feature) =>
          ["inventory_count", "operational_analytics"].includes(feature.key),
        ),
      },
      {
        key: "production",
        title: "Виробництво · лише PRO",
        features: features.filter((feature) =>
          [
            "technology_management",
            "production_orders",
            "auto_replenishment",
          ].includes(feature.key),
        ),
      },
      {
        key: "reports_integrations",
        title: "Звіти та інтеграції",
        features: features.filter((feature) =>
          ["advanced_reports", "product_labels", "ksef_integration"].includes(
            feature.key,
          ),
        ),
      },
    ]
  }

  const groups: Array<{
    key: string
    title: string
    match: (key: string) => boolean
  }> = [
    {
      key: "news_documents",
      title: "Dokumenty i aktualności",
      match: (key) =>
        key.startsWith("company_news.") ||
        key.startsWith("documents.") ||
        key.startsWith("employee_documents."),
    },
    {
      key: "scheduling",
      title: "Grafik i urlopy",
      match: (key) =>
        key.startsWith("work_schedule.") ||
        key.startsWith("vacations_sick.") ||
        key.startsWith("shift_entries."),
    },
    {
      key: "communication",
      title: "Komunikacja",
      match: (key) =>
        key.startsWith("rooms.") ||
        key.startsWith("chat.messages.") ||
        key.startsWith("direct.messages.") ||
        key.startsWith("notifications."),
    },
    {
      key: "access_exports",
      title: "Access & Exports",
      match: (key) => key.startsWith("qr.") || key.startsWith("exports."),
    },
  ]

  const remaining = [...features]
  const out: FeatureGroup[] = []

  for (const group of groups) {
    const matched = remaining.filter((feature) => group.match(feature.key))
    if (matched.length === 0) continue
    out.push({
      key: group.key,
      title: group.title,
      features: matched,
    })
  }

  const assigned = new Set(
    out.flatMap((group) => group.features.map((feature) => feature.key)),
  )
  const leftovers = features.filter((feature) => !assigned.has(feature.key))
  if (leftovers.length > 0) {
    out.push({
      key: "other",
      title: "Other",
      features: leftovers,
    })
  }

  return out
}

function buildFallbackOptions(): LicenseOptionsResponse {
  return {
    statuses: [
      "active",
      "expired_grace",
      "expired_limited",
      "expired_critical",
      "unlicensed",
      "suspended",
      "revoked",
    ],
    bootstrap: {
      issuer_default: "lms.dbg-wms.local",
      audiences: {
        wms: "dbg-wms-runtime",
        hrm: "dbg-one-hrm-runtime",
      },
    },
    products: {
      wms: {
        product: "wms",
        default_features: {
          technology_management: true,
          inventory_count: true,
          advanced_reports: true,
          advanced_analytics: true,
        },
        default_limits: { exports_per_day: 100 },
        feature_definitions: [
          {
            key: "technology_management",
            label: "Technology Management",
            description: "Manage technology and production-related flows.",
          },
          {
            key: "inventory_count",
            label: "Inventory Management",
            description:
              "Manage inventory counting plans, tasks, and anomaly monitoring.",
          },
          {
            key: "advanced_reports",
            label: "Advanced Reports",
            description: "Use advanced reporting modules and exports.",
          },
          {
            key: "advanced_analytics",
            label: "Advanced Analytics",
            description: "Access analytical dashboards and extended metrics.",
          },
        ],
        limit_definitions: [
          {
            key: "exports_per_day",
            label: "Exports per day",
            description: "Maximum daily export operations.",
            default_value: 100,
            min_value: 1,
          },
        ],
      },
      hrm: {
        product: "hrm",
        default_features: {
          "company_news.read": true,
          "company_news.write": true,
          "documents.read": true,
          "documents.write": true,
          "employee_documents.read": true,
          "employee_documents.write": true,
          "work_schedule.read": true,
          "work_schedule.write": true,
          "vacations_sick.read": true,
          "vacations_sick.write": true,
          "shift_entries.read": true,
          "shift_entries.write": true,
          "rooms.read": true,
          "rooms.write": true,
          "chat.messages.read": true,
          "chat.messages.write": true,
          "direct.messages.read": true,
          "direct.messages.write": true,
          "notifications.read": true,
          "notifications.write": true,
          "qr.issue": true,
          "qr.verify": true,
          "exports.pdf": true,
          "exports.xlsx": true,
          "exports.xml": true,
        },
        default_limits: {},
        feature_definitions: [
          {
            key: "company_news.read",
            label: "Company News Read",
            description: "View company news feed.",
          },
          {
            key: "company_news.write",
            label: "Company News Write",
            description: "Create and edit company news posts.",
          },
          {
            key: "documents.read",
            label: "Documents Read",
            description: "View shared HR documents.",
          },
          {
            key: "documents.write",
            label: "Documents Write",
            description: "Upload and remove shared HR documents.",
          },
          {
            key: "employee_documents.read",
            label: "Employee Documents Read",
            description: "View employee document attachments.",
          },
          {
            key: "employee_documents.write",
            label: "Employee Documents Write",
            description: "Upload and remove employee documents.",
          },
          {
            key: "work_schedule.read",
            label: "Work Schedule Read",
            description: "View work schedules and details.",
          },
          {
            key: "work_schedule.write",
            label: "Work Schedule Write",
            description: "Create, edit and confirm schedules.",
          },
          {
            key: "vacations_sick.read",
            label: "Vacations Read",
            description: "View vacation and sick leave data.",
          },
          {
            key: "vacations_sick.write",
            label: "Vacations Write",
            description: "Submit and manage vacation workflows.",
          },
          {
            key: "shift_entries.read",
            label: "Shift Entries Read",
            description: "View shift entry records.",
          },
          {
            key: "shift_entries.write",
            label: "Shift Entries Write",
            description: "Open and manage shift entries.",
          },
          {
            key: "rooms.read",
            label: "Rooms Read",
            description: "View chat rooms and room lists.",
          },
          {
            key: "rooms.write",
            label: "Rooms Write",
            description: "Create, edit and remove rooms.",
          },
          {
            key: "chat.messages.read",
            label: "Chat Read",
            description: "Read room chat messages.",
          },
          {
            key: "chat.messages.write",
            label: "Chat Write",
            description: "Send, edit and react to room chat messages.",
          },
          {
            key: "direct.messages.read",
            label: "Direct Messages Read",
            description: "Read direct conversations.",
          },
          {
            key: "direct.messages.write",
            label: "Direct Messages Write",
            description: "Send, edit and react in direct chat.",
          },
          {
            key: "notifications.read",
            label: "Notifications Read",
            description: "View notifications and notification drawer.",
          },
          {
            key: "notifications.write",
            label: "Notifications Write",
            description: "Mutate notification state and cleanup.",
          },
          {
            key: "qr.issue",
            label: "QR Issue",
            description: "Generate QR codes and badges.",
          },
          {
            key: "qr.verify",
            label: "QR Verify",
            description: "Verify QR codes and scans.",
          },
          {
            key: "exports.pdf",
            label: "PDF Export",
            description: "Allow PDF exports and generated reports.",
          },
          {
            key: "exports.xlsx",
            label: "XLSX Export",
            description: "Allow spreadsheet exports.",
          },
          {
            key: "exports.xml",
            label: "XML Export",
            description: "Allow XML exports and integrations.",
          },
        ],
        limit_definitions: [],
      },
    },
  }
}

function getCredentialStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "revoked":
      return "red"
    default:
      return "gray"
  }
}

function slugifyInstallationPart(value?: string | null) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function buildInstallationId(input: {
  customerCode?: string | null
  customerId?: string | null
  environment?: string | null
  runtimeTarget?: RuntimeTarget
  existingInstallations?: InstallationCredentialItem[]
}) {
  const customerPart =
    slugifyInstallationPart(input.customerCode) ||
    slugifyInstallationPart(input.customerId)?.slice(0, 8) ||
    "client"
  const environmentPart =
    slugifyInstallationPart(input.environment) || "production"
  const runtimeTarget = input.runtimeTarget === "hrm" ? "hrm" : "wms"
  const base = `${customerPart}-${environmentPart}-${runtimeTarget}`
  const used = new Set(
    (input.existingInstallations ?? []).map((item) =>
      String(item.installation_id ?? "")
        .trim()
        .toLowerCase(),
    ),
  )

  for (let index = 1; index <= 999; index += 1) {
    const suffix = String(index).padStart(2, "0")
    const candidate = `${base}-${suffix}`
    if (!used.has(candidate)) return candidate
  }

  return `${base}-${Date.now()}`
}

function LicensesAdminPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const revokeConfirm = useDisclosure()
  const panelBg = useColorModeValue("white", "gray.800")
  const mutedTextColor = useColorModeValue("gray.500", "gray.300")
  const tableHeadBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")
  const dangerBorderColor = useColorModeValue("red.200", "red.500")
  const [status, setStatus] = useState("")
  const [productFilter, setProductFilter] = useState<"" | RuntimeTarget>("")
  const [customerIdFilter, setCustomerIdFilter] = useState("")
  const [selectedLicenseId, setSelectedLicenseId] = useState("")
  const [validationError, setValidationError] = useState<string | null>(null)
  const fallbackOptions = useMemo(() => buildFallbackOptions(), [])
  const [form, setForm] = useState<UpsertPayload>({
    customer_id: "",
    customer_code: "",
    license_id: "",
    product: "wms",
    plan: "PRO",
    status: "active",
    starts_at: new Date().toISOString().slice(0, 10),
    expires_at: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    grace_days: 14,
    offline_max_days: 14,
    critical_after_limited_days: 30,
    capability_enforcement_mode: "enforce",
    emergency_finalize_enabled: false,
    contract_version: 2,
    module_overrides: {},
    features: cloneBooleanMap(fallbackOptions.products.wms.default_features),
    limits: cloneNumberMap(fallbackOptions.products.wms.default_limits),
  })
  const [renewDate, setRenewDate] = useState("")
  const [selectedCustomer, setSelectedCustomer] = useState("")
  const [issuedToken, setIssuedToken] = useState<{
    token: string
    jti: string
    kid: string
    expiresAt: string
  } | null>(null)
  const [installationForm, setInstallationForm] = useState({
    installation_id: "",
    environment: "production",
    runtime_target: "wms" as RuntimeTarget,
  })
  const [issuedSecrets, setIssuedSecrets] = useState<{
    installationId: string
    runtimeSharedKey: string
    webhookSharedKey: string
  } | null>(null)
  const apiBase = String(import.meta.env.VITE_API_DOMAIN ?? OpenAPI.BASE ?? "")
    .trim()
    .replace(/\/+$/, "")

  const optionsQuery = useQuery({
    queryKey: ["lms", "licenses", "options"],
    queryFn: () => apiFetch<LicenseOptionsResponse>("/v1/lms/licenses/options"),
  })

  const licenseOptions = optionsQuery.data ?? fallbackOptions
  const statusOptions =
    licenseOptions.statuses.length > 0
      ? licenseOptions.statuses
      : fallbackOptions.statuses
  const currentProductContract = useMemo(
    () =>
      normalizeProductContract(
        form.product,
        licenseOptions.products[form.product] ??
          fallbackOptions.products[form.product],
      ),
    [fallbackOptions.products, form.product, licenseOptions.products],
  )
  const featureGroups = useMemo(
    () =>
      buildFeatureGroups(
        form.product,
        currentProductContract.feature_definitions,
      ),
    [form.product, currentProductContract.feature_definitions],
  )

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    params.set("limit", "100")
    if (status) params.set("status", status)
    if (productFilter) params.set("product", productFilter)
    if (customerIdFilter.trim())
      params.set("customer_id", customerIdFilter.trim())
    return params.toString()
  }, [status, productFilter, customerIdFilter])

  const listQuery = useQuery({
    queryKey: ["lms", "licenses", queryString],
    queryFn: () =>
      apiFetch<LicenseListResponse>(`/v1/lms/licenses?${queryString}`),
  })
  const customersQuery = useQuery({
    queryKey: ["lms", "customers", "active"],
    queryFn: () =>
      apiFetch<CustomerListResponse>(
        "/v1/lms/customers?limit=200&status=active",
      ),
  })

  const expiringSoonCount = useMemo(() => {
    const now = Date.now()
    const max = now + 30 * 24 * 60 * 60 * 1000
    return (listQuery.data?.items ?? []).filter((item) => {
      const exp = new Date(item.expires_at).getTime()
      return Number.isFinite(exp) && exp >= now && exp <= max
    }).length
  }, [listQuery.data?.items])

  const revokedCount = useMemo(
    () =>
      (listQuery.data?.items ?? []).filter((item) => item.status === "revoked")
        .length,
    [listQuery.data?.items],
  )

  const metricsQuery = useQuery({
    queryKey: ["lms", "license", "metrics", selectedLicenseId],
    queryFn: () =>
      apiFetch<LicenseMetricsResponse>(
        `/v1/lms/licenses/${selectedLicenseId}/metrics`,
      ),
    enabled: selectedLicenseId.length > 0,
  })

  const detailQuery = useQuery({
    queryKey: ["lms", "license", "detail", selectedLicenseId],
    queryFn: () =>
      apiFetch<LicenseDetailResponse>(`/v1/lms/licenses/${selectedLicenseId}`),
    enabled: selectedLicenseId.length > 0,
  })

  const installationsQuery = useQuery({
    queryKey: ["lms", "license", "installations", selectedLicenseId],
    queryFn: () =>
      apiFetch<InstallationListResponse>(
        `/v1/lms/licenses/${selectedLicenseId}/installations`,
      ),
    enabled: selectedLicenseId.length > 0,
  })

  const selectedLicense = useMemo(
    () =>
      detailQuery.data?.license ??
      (listQuery.data?.items ?? []).find(
        (item) => item.license_id === selectedLicenseId,
      ) ??
      null,
    [detailQuery.data?.license, listQuery.data?.items, selectedLicenseId],
  )

  useEffect(() => {
    if (!selectedLicense?.product) return
    setInstallationForm((current) => ({
      ...current,
      runtime_target: selectedLicense.product,
    }))
  }, [selectedLicense?.product])

  const bootstrapSnippet = useMemo(() => {
    if (!issuedSecrets || !selectedLicense) {
      return null
    }
    return [
      ...legacyEnforcementLinesForProduct(selectedLicense.product),
      `LMS_REFRESH_URL=${apiBase}/api/v1/license/refresh`,
      `LMS_PUBLIC_KEY_URL=${apiBase}/api/v1/license/public-key`,
      `LMS_RUNTIME_SHARED_KEY=${issuedSecrets.runtimeSharedKey}`,
      `LMS_WEBHOOK_SHARED_KEY=${issuedSecrets.webhookSharedKey}`,
      `LMS_RUNTIME_INSTALLATION_ID=${issuedSecrets.installationId}`,
      `LMS_ENVIRONMENT_LABEL=${installationForm.environment || "production"}`,
      "LMS_SYNC_TIMEOUT_SECONDS=10",
      "LMS_SYNC_INTERVAL_SECONDS=3600",
      "LMS_REFRESH_SKEW_SECONDS=900",
      "LICENSE_PUBLIC_KEYS_DIR=./license/public_keys",
      "LICENSE_JWT_PATH=./license/runtime.jwt",
      `LICENSE_ISSUER=${licenseOptions.bootstrap.issuer_default || fallbackOptions.bootstrap.issuer_default}`,
      `LICENSE_AUDIENCE=${licenseOptions.bootstrap.audiences[selectedLicense.product] || fallbackOptions.bootstrap.audiences[selectedLicense.product]}`,
    ].join("\n")
  }, [
    apiBase,
    fallbackOptions.bootstrap.audiences,
    fallbackOptions.bootstrap.issuer_default,
    installationForm.environment,
    issuedSecrets,
    licenseOptions.bootstrap.audiences,
    licenseOptions.bootstrap.issuer_default,
    selectedLicense,
  ])

  const suggestedInstallationId = useMemo(
    () =>
      buildInstallationId({
        customerCode: selectedLicense?.customer_code,
        customerId: selectedLicense?.customer_id,
        environment: installationForm.environment,
        runtimeTarget: installationForm.runtime_target,
        existingInstallations: installationsQuery.data?.items ?? [],
      }),
    [
      installationForm.environment,
      installationForm.runtime_target,
      installationsQuery.data?.items,
      selectedLicense?.customer_code,
      selectedLicense?.customer_id,
    ],
  )

  const issueMutation = useMutation({
    mutationFn: (licenseId: string) =>
      apiFetch<{
        license_jwt: string
        jti: string
        kid: string
        expires_at: string
      }>(`/v1/lms/licenses/${licenseId}/issue`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      setIssuedToken({
        token: data.license_jwt,
        jti: data.jti,
        kid: data.kid,
        expiresAt: data.expires_at,
      })
      toast({
        title: "Licencja została wystawiona",
        description: `jti: ${data.jti}`,
        status: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", selectedLicenseId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się wystawić licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (licenseId: string) =>
      apiFetch<{ status: string }>(`/v1/lms/licenses/${licenseId}/revoke`, {
        method: "POST",
        body: JSON.stringify({ reason: "manual_admin_revoke" }),
      }),
    onSuccess: () => {
      toast({
        title: "Licencja została unieważniona",
        status: "warning",
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "licenses"] })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", selectedLicenseId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się unieważnić licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const quickPlanMutation = useMutation({
    mutationFn: ({
      licenseId,
      plan,
      expectedRevision,
    }: {
      licenseId: string
      plan: "BASIC" | "PRO"
      expectedRevision: number
    }) =>
      apiFetch<LicenseItem>(`/v1/lms/licenses/${licenseId}/plan`, {
        method: "POST",
        body: JSON.stringify({ plan, expected_revision: expectedRevision }),
      }),
    onSuccess: (data) => {
      toast({
        title: "Plan został zaktualizowany",
        description: `${data.license_id}: ${data.plan}. Zapisano w LMS; zastosowanie w WMS nastąpi po synchronizacji.`,
        status: "success",
        duration: 2500,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "licenses"] })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", data.license_id],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", data.license_id],
      })
      if (selectedLicenseId === data.license_id) {
        setForm(buildUpsertPayloadFromLicense(data))
      }
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się zaktualizować planu",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const upsertMutation = useMutation({
    mutationFn: (payload: UpsertPayload) =>
      apiFetch<LicenseItem>(`/v1/lms/licenses`, {
        method: "POST",
        body: JSON.stringify({
          ...serializeLicensePayload(
            payload,
            licenseOptions.products[payload.product],
          ),
          license_id: payload.license_id?.trim() || "",
          starts_at: `${payload.starts_at}T00:00:00Z`,
          expires_at: `${payload.expires_at}T00:00:00Z`,
        }),
      }),
    onSuccess: (data) => {
      toast({
        title: "Licencja została zapisana",
        description: `${data.license_id}. Zapisano w LMS; zastosowanie w WMS nastąpi po synchronizacji.`,
        status: "success",
        duration: 3000,
      })
      setSelectedLicenseId(data.license_id)
      setForm(buildUpsertPayloadFromLicense(data))
      queryClient.invalidateQueries({ queryKey: ["lms", "licenses"] })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", data.license_id],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", data.license_id],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się zapisać licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const renewMutation = useMutation({
    mutationFn: ({
      licenseId,
      expiresAt,
    }: {
      licenseId: string
      expiresAt: string
    }) =>
      apiFetch<LicenseItem>(`/v1/lms/licenses/${licenseId}/renew`, {
        method: "POST",
        body: JSON.stringify({ expires_at: `${expiresAt}T00:00:00Z` }),
      }),
    onSuccess: () => {
      toast({
        title: "Licencja została odnowiona",
        status: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "licenses"] })
      if (selectedLicenseId) {
        queryClient.invalidateQueries({
          queryKey: ["lms", "license", "metrics", selectedLicenseId],
        })
        queryClient.invalidateQueries({
          queryKey: ["lms", "license", "detail", selectedLicenseId],
        })
      }
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się odnowić licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const createInstallationMutation = useMutation({
    mutationFn: ({
      licenseId,
      payload,
    }: {
      licenseId: string
      payload: { installation_id: string; environment: string }
    }) =>
      apiFetch<InstallationSecretsResponse>(
        `/v1/lms/licenses/${licenseId}/installations`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: (data) => {
      setIssuedSecrets({
        installationId: data.credential.installation_id,
        runtimeSharedKey: data.secrets.runtime_shared_key,
        webhookSharedKey: data.secrets.webhook_shared_key,
      })
      setInstallationForm((current) => ({
        installation_id: "",
        environment: "production",
        runtime_target: current.runtime_target,
      }))
      toast({
        title: "Instalacja została utworzona",
        description: data.credential.installation_id,
        status: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "installations", selectedLicenseId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się utworzyć instalacji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const rotateInstallationMutation = useMutation({
    mutationFn: ({
      licenseId,
      installationId,
    }: {
      licenseId: string
      installationId: string
    }) =>
      apiFetch<InstallationSecretsResponse>(
        `/v1/lms/licenses/${licenseId}/installations/${installationId}/rotate`,
        {
          method: "POST",
        },
      ),
    onSuccess: (data) => {
      setIssuedSecrets({
        installationId: data.credential.installation_id,
        runtimeSharedKey: data.secrets.runtime_shared_key,
        webhookSharedKey: data.secrets.webhook_shared_key,
      })
      toast({
        title: "Sekrety instalacji zostały obrócone",
        description: data.credential.installation_id,
        status: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "installations", selectedLicenseId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się obrócić sekretów",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const revokeInstallationMutation = useMutation({
    mutationFn: ({
      licenseId,
      installationId,
    }: {
      licenseId: string
      installationId: string
    }) =>
      apiFetch<{ credential: InstallationCredentialItem }>(
        `/v1/lms/licenses/${licenseId}/installations/${installationId}/revoke`,
        {
          method: "POST",
        },
      ),
    onSuccess: (_, vars) => {
      toast({
        title: "Instalacja została unieważniona",
        description: vars.installationId,
        status: "warning",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "detail", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "metrics", selectedLicenseId],
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "license", "installations", selectedLicenseId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się unieważnić instalacji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  return (
    <Container maxW="9xl" py={4}>
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
          <Heading size="lg">Operacje na licencjach</Heading>
          <Text color={mutedTextColor} mt={1}>
            Zarządzaj cyklem życia licencji, tokenami JWT oraz poświadczeniami
            na poziomie instalacji.
          </Text>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={3}
            mt={4}
          >
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Załadowane licencje</StatLabel>
                <StatNumber>{listQuery.data?.total ?? 0}</StatNumber>
                <StatHelpText>W zakresie bieżącego filtra</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Wygasają w ciągu 30 dni</StatLabel>
                <StatNumber
                  color={expiringSoonCount > 0 ? "orange.500" : "green.500"}
                >
                  {expiringSoonCount}
                </StatNumber>
                <StatHelpText>Wymagają zaplanowania odnowienia</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Unieważnione</StatLabel>
                <StatNumber color={revokedCount > 0 ? "red.500" : "gray.700"}>
                  {revokedCount}
                </StatNumber>
                <StatHelpText>
                  Działania związane z bezpieczeństwem
                </StatHelpText>
              </Stat>
            </Box>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
            <GridItem>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  {statusOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Produkt</FormLabel>
                <Select
                  value={productFilter}
                  onChange={(e) =>
                    setProductFilter(
                      (e.target.value as RuntimeTarget | "") ?? "",
                    )
                  }
                >
                  <option value="">Wszystkie</option>
                  <option value="wms">wms</option>
                  <option value="hrm">hrm</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Klient</FormLabel>
                <Select
                  placeholder="Wybierz klienta"
                  value={selectedCustomer}
                  onChange={(e) => {
                    const val = e.target.value
                    setSelectedCustomer(val)
                    setCustomerIdFilter(val)
                    const c = (customersQuery.data?.items ?? []).find(
                      (it) => it.customer_id === val,
                    )
                    if (c) {
                      setForm((s) => ({
                        ...s,
                        customer_id: c.customer_id,
                        customer_code: c.code || s.customer_code,
                      }))
                    }
                  }}
                >
                  {(customersQuery.data?.items ?? []).map((c) => (
                    <option key={c.customer_id} value={c.customer_id}>
                      {c.code || c.customer_id} - {c.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <HStack alignSelf="end" h="full" align="end">
                <Button
                  w="full"
                  variant="outline"
                  onClick={() => {
                    setStatus("")
                    setProductFilter("")
                    setSelectedCustomer("")
                    setCustomerIdFilter("")
                  }}
                >
                  Reset filters
                </Button>
                <Button
                  w="full"
                  onClick={() =>
                    queryClient.invalidateQueries({
                      queryKey: ["lms", "licenses"],
                    })
                  }
                >
                  Reload
                </Button>
              </HStack>
            </GridItem>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Heading size="sm" mb={3}>
            Upsert License
          </Heading>
          {validationError ? (
            <Alert status="error" mb={3} borderRadius="md">
              <AlertIcon />
              {validationError}
            </Alert>
          ) : null}
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
            <GridItem>
              <Input
                placeholder="customer_id"
                value={form.customer_id}
                onChange={(e) =>
                  setForm((s) => ({ ...s, customer_id: e.target.value }))
                }
              />
            </GridItem>
            <GridItem>
              <Input
                placeholder="customer_code"
                value={form.customer_code}
                onChange={(e) =>
                  setForm((s) => ({ ...s, customer_code: e.target.value }))
                }
              />
            </GridItem>
            <GridItem>
              <Input
                minW={0}
                value={form.license_id || "Auto-generated by backend"}
                isReadOnly
              />
            </GridItem>
            <GridItem>
              <Select
                value={form.product}
                onChange={(e) => {
                  const product = (e.target.value as RuntimeTarget) || "wms"
                  const contract =
                    licenseOptions.products[product] ??
                    fallbackOptions.products[product]
                  setForm((current) => ({
                    ...current,
                    product,
                    features: cloneBooleanMap(contract.default_features),
                    limits: cloneNumberMap(contract.default_limits),
                  }))
                }}
              >
                <option value="wms">WMS</option>
                <option value="hrm">HRM</option>
              </Select>
            </GridItem>
            <GridItem>
              <Select
                value={form.plan}
                onChange={(e) =>
                  setForm((s) => ({ ...s, plan: e.target.value }))
                }
              >
                <option value="BASIC">BASIC</option>
                <option value="PRO">PRO</option>
                {form.product !== "wms" ? (
                  <option value="ENTERPRISE">ENTERPRISE</option>
                ) : null}
              </Select>
            </GridItem>
            <GridItem>
              <Select
                value={form.status}
                onChange={(e) =>
                  setForm((s) => ({ ...s, status: e.target.value }))
                }
              >
                {statusOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </GridItem>
            <GridItem>
              <Input
                type="date"
                value={form.starts_at}
                onChange={(e) =>
                  setForm((s) => ({ ...s, starts_at: e.target.value }))
                }
              />
            </GridItem>
            <GridItem>
              <Input
                type="date"
                value={form.expires_at}
                onChange={(e) =>
                  setForm((s) => ({ ...s, expires_at: e.target.value }))
                }
              />
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Dni karencji</FormLabel>
                <NumberInput
                  min={1}
                  value={form.grace_days}
                  onChange={(_, v) =>
                    setForm((s) => ({
                      ...s,
                      grace_days: Number.isFinite(v) ? v : 14,
                    }))
                  }
                >
                  <NumberInputField placeholder="grace_days" />
                </NumberInput>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Maks. dni offline</FormLabel>
                <NumberInput
                  min={1}
                  value={form.offline_max_days}
                  onChange={(_, v) =>
                    setForm((s) => ({
                      ...s,
                      offline_max_days: Number.isFinite(v) ? v : 14,
                    }))
                  }
                >
                  <NumberInputField placeholder="offline_max_days" />
                </NumberInput>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Krytyczne po dniach ograniczenia</FormLabel>
                <NumberInput
                  min={1}
                  value={form.critical_after_limited_days}
                  onChange={(_, v) =>
                    setForm((s) => ({
                      ...s,
                      critical_after_limited_days: Number.isFinite(v) ? v : 30,
                    }))
                  }
                >
                  <NumberInputField placeholder="critical_after_limited_days" />
                </NumberInput>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Tryb egzekwowania możliwości</FormLabel>
                <Select
                  value={form.capability_enforcement_mode}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      capability_enforcement_mode:
                        e.target.value === "audit" ? "audit" : "enforce",
                    }))
                  }
                >
                  <option value="enforce">egzekwuj</option>
                  <option value="audit">audyt</option>
                </Select>
              </FormControl>
            </GridItem>
            {currentProductContract.limit_definitions.map((limit) => (
              <GridItem key={limit.key}>
                <FormControl>
                  <FormLabel>{limit.label}</FormLabel>
                  <NumberInput
                    min={limit.min_value || 0}
                    value={form.limits[limit.key] ?? limit.default_value}
                    onChange={(_, v) =>
                      setForm((s) => ({
                        ...s,
                        limits: {
                          ...s.limits,
                          [limit.key]: Number.isFinite(v)
                            ? v
                            : limit.default_value,
                        },
                      }))
                    }
                  >
                    <NumberInputField placeholder={limit.key} />
                  </NumberInput>
                  {limit.description ? (
                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                      {limit.description}
                    </Text>
                  ) : null}
                </FormControl>
              </GridItem>
            ))}
          </Grid>
          <Box mt={3}>
            <Checkbox
              isChecked={form.emergency_finalize_enabled}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  emergency_finalize_enabled: e.target.checked,
                }))
              }
            >
              Emergency finalize enabled for suspended/revoked/unlicensed
            </Checkbox>
          </Box>
          <VStack mt={3} spacing={4} align="stretch">
            {form.product === "wms" ? (
              <Text fontSize="sm" color={mutedTextColor}>
                BASIC містить інвентаризацію, аналітику, звіти, етикетки та
                інтеграції. Виробничі модулі доступні лише в PRO. Зміни
                застосуються у WMS після синхронізації ліцензії.
              </Text>
            ) : null}
            {featureGroups.map((group) => (
              <Box key={group.key} borderWidth="1px" borderRadius="lg" p={3}>
                <Heading size="xs" mb={3}>
                  {group.title}
                </Heading>
                <Grid
                  templateColumns={{
                    base: "1fr",
                    md: "repeat(2, 1fr)",
                    xl: "repeat(3, 1fr)",
                  }}
                  gap={4}
                >
                  {group.features.map((feature) => (
                    <Box key={feature.key} minW="0">
                      <Checkbox
                        isChecked={Boolean(form.features[feature.key])}
                        isDisabled={
                          feature.editable === false ||
                          (form.product === "wms" &&
                            !(form.plan === "BASIC"
                              ? feature.basic_included
                              : feature.pro_included))
                        }
                        onChange={(e) =>
                          setForm((s) => {
                            const moduleOverrides = { ...s.module_overrides }
                            if (e.target.checked)
                              delete moduleOverrides[feature.key]
                            else moduleOverrides[feature.key] = false
                            return {
                              ...s,
                              module_overrides: moduleOverrides,
                              features: {
                                ...s.features,
                                [feature.key]: e.target.checked,
                              },
                            }
                          })
                        }
                      >
                        {feature.label}
                      </Checkbox>
                      {feature.description ? (
                        <Text fontSize="xs" color={mutedTextColor} mt={1}>
                          {feature.description}
                        </Text>
                      ) : null}
                      {form.product === "wms" &&
                      !(form.plan === "BASIC"
                        ? feature.basic_included
                        : feature.pro_included) ? (
                        <Text fontSize="xs" color="orange.400" mt={1}>
                          Dostępne wyłącznie w planie PRO.
                        </Text>
                      ) : form.product === "wms" &&
                        form.features[feature.key] === false ? (
                        <Text fontSize="xs" color="orange.400" mt={1}>
                          Вимкнено для цієї ліцензії.
                        </Text>
                      ) : feature.dependencies?.length ? (
                        <Text fontSize="xs" color={mutedTextColor} mt={1}>
                          Zależne od: {feature.dependencies.join(", ")}.
                        </Text>
                      ) : null}
                    </Box>
                  ))}
                </Grid>
              </Box>
            ))}
          </VStack>
          <HStack mt={3}>
            <Button
              colorScheme="blue"
              isLoading={upsertMutation.isPending}
              isDisabled={
                form.product === "wms" &&
                (!optionsQuery.isSuccess ||
                  optionsQuery.isError ||
                  !currentProductContract.modules?.length)
              }
              onClick={() => {
                setValidationError(null)
                const uuidRe =
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
                if (!uuidRe.test(form.customer_id)) {
                  setValidationError("customer_id must be a valid UUID")
                  return
                }
                if (
                  new Date(form.expires_at).getTime() <=
                  new Date(form.starts_at).getTime()
                ) {
                  setValidationError(
                    "expires_at must be greater than starts_at",
                  )
                  return
                }
                upsertMutation.mutate(form)
              }}
            >
              Save License
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedLicenseId(form.license_id)}
              isDisabled={!form.license_id}
            >
              Open Metrics
            </Button>
          </HStack>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
          <TableContainer>
            <Table size="sm">
              <Thead bg={tableHeadBg}>
                <Tr>
                  <Th>Licencja</Th>
                  <Th>Klient</Th>
                  <Th>Produkt</Th>
                  <Th>Plan</Th>
                  <Th>Status</Th>
                  <Th>Wygasa</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {listQuery.isLoading ? (
                  <Tr>
                    <Td colSpan={7}>
                      <Spinner size="sm" />
                    </Td>
                  </Tr>
                ) : (listQuery.data?.items ?? []).length === 0 ? (
                  <Tr>
                    <Td
                      colSpan={7}
                      textAlign="center"
                      py={6}
                      color={mutedTextColor}
                    >
                      No licenses found for current filters.
                    </Td>
                  </Tr>
                ) : (
                  (listQuery.data?.items ?? []).map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td fontFamily="mono" fontSize="xs">
                        {item.license_id}
                      </Td>
                      <Td>{item.customer_code || item.customer_id}</Td>
                      <Td>
                        <Badge
                          colorScheme={item.product === "hrm" ? "cyan" : "blue"}
                          variant="subtle"
                        >
                          {item.product}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge variant="subtle" colorScheme="purple">
                          {item.plan}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={getLicenseStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                      </Td>
                      <Td>{new Date(item.expires_at).toLocaleString()}</Td>
                      <Td>
                        <HStack>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              setForm(buildUpsertPayloadFromLicense(item))
                              setSelectedLicenseId(item.license_id)
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            onClick={() =>
                              setSelectedLicenseId(item.license_id)
                            }
                          >
                            Metrics
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="orange"
                            variant={
                              item.plan === "BASIC" ? "solid" : "outline"
                            }
                            isDisabled={item.plan === "BASIC"}
                            isLoading={
                              quickPlanMutation.isPending &&
                              quickPlanMutation.variables?.licenseId ===
                                item.license_id &&
                              quickPlanMutation.variables?.plan === "BASIC"
                            }
                            onClick={() =>
                              quickPlanMutation.mutate({
                                licenseId: item.license_id,
                                plan: "BASIC",
                                expectedRevision: item.license_revision,
                              })
                            }
                          >
                            BASIC
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="green"
                            variant={item.plan === "PRO" ? "solid" : "outline"}
                            isDisabled={item.plan === "PRO"}
                            isLoading={
                              quickPlanMutation.isPending &&
                              quickPlanMutation.variables?.licenseId ===
                                item.license_id &&
                              quickPlanMutation.variables?.plan === "PRO"
                            }
                            onClick={() =>
                              quickPlanMutation.mutate({
                                licenseId: item.license_id,
                                plan: "PRO",
                                expectedRevision: item.license_revision,
                              })
                            }
                          >
                            PRO
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {selectedLicenseId ? (
          <Box borderWidth="1px" borderRadius="xl" p={4}>
            <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
              <Heading size="sm">Metrics: {selectedLicenseId}</Heading>
              <HStack flexWrap="wrap" gap={2}>
                <Input
                  type="date"
                  value={renewDate}
                  onChange={(e) => setRenewDate(e.target.value)}
                  size="sm"
                  maxW="180px"
                />
                <Button
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  onClick={() =>
                    renewMutation.mutate({
                      licenseId: selectedLicenseId,
                      expiresAt: renewDate,
                    })
                  }
                  isLoading={renewMutation.isPending}
                  isDisabled={!renewDate}
                >
                  Renew
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => issueMutation.mutate(selectedLicenseId)}
                  isLoading={issueMutation.isPending}
                >
                  Issue JWT
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={revokeConfirm.onOpen}
                  isLoading={revokeMutation.isPending}
                >
                  Revoke
                </Button>
              </HStack>
            </HStack>
            {metricsQuery.isLoading ? (
              <Spinner size="sm" />
            ) : (
              <VStack align="stretch" spacing={2}>
                <HStack>
                  <Text>Produkt:</Text>
                  <Badge
                    colorScheme={
                      metricsQuery.data?.license?.product === "hrm"
                        ? "cyan"
                        : "blue"
                    }
                  >
                    {metricsQuery.data?.license?.product ?? "-"}
                  </Badge>
                </HStack>
                <HStack>
                  <Text>Status licencji:</Text>
                  <Badge
                    colorScheme={getLicenseStatusColor(
                      metricsQuery.data?.license?.status,
                    )}
                  >
                    {metricsQuery.data?.license?.status ?? "-"}
                  </Badge>
                </HStack>
                <HStack>
                  <Text>Tryb uprawnień:</Text>
                  <Badge
                    colorScheme={
                      metricsQuery.data?.license
                        ?.capability_enforcement_mode === "audit"
                        ? "yellow"
                        : "green"
                    }
                  >
                    {metricsQuery.data?.license?.capability_enforcement_mode ??
                      "enforce"}
                  </Badge>
                </HStack>
                <HStack>
                  <Text>Awaryjne zamknięcie:</Text>
                  <Badge
                    colorScheme={
                      metricsQuery.data?.license?.emergency_finalize_enabled
                        ? "orange"
                        : "gray"
                    }
                  >
                    {metricsQuery.data?.license?.emergency_finalize_enabled
                      ? "enabled"
                      : "disabled"}
                  </Badge>
                </HStack>
                <HStack>
                  <Text>Zarejestrowane instalacje:</Text>
                  <Badge colorScheme="blue">
                    {installationsQuery.data?.items?.length ?? 0}
                  </Badge>
                </HStack>
                <Box>
                  <Text fontWeight="semibold">Dzisiejsze użycie</Text>
                  {(metricsQuery.data?.usage_today ?? []).length === 0 ? (
                    <Text color={mutedTextColor}>
                      Brak danych użycia na dziś.
                    </Text>
                  ) : (
                    (metricsQuery.data?.usage_today ?? []).map((u) => (
                      <Text key={u.metric_key}>
                        {u.metric_key}: {u.count}
                      </Text>
                    ))
                  )}
                </Box>
                <Box mt={2}>
                  <Text fontWeight="semibold" mb={2}>
                    Installation Credentials
                  </Text>
                  <Grid
                    templateColumns={{ base: "1fr", md: "2fr 1fr 1fr auto" }}
                    gap={3}
                    mb={3}
                  >
                    <GridItem>
                      <Input
                        placeholder="installation_id"
                        value={installationForm.installation_id}
                        onChange={(e) =>
                          setInstallationForm((s) => ({
                            ...s,
                            installation_id: e.target.value,
                          }))
                        }
                      />
                      <Button
                        variant="outline"
                        onClick={() =>
                          setInstallationForm((s) => ({
                            ...s,
                            installation_id: suggestedInstallationId,
                          }))
                        }
                      >
                        Generate ID
                      </Button>
                    </GridItem>
                    <GridItem>
                      <Select
                        value={installationForm.runtime_target}
                        isDisabled
                      >
                        <option value="wms">WMS runtime</option>
                        <option value="hrm">HRM runtime</option>
                      </Select>
                    </GridItem>
                    <GridItem>
                      <Select
                        value={installationForm.environment}
                        onChange={(e) =>
                          setInstallationForm((s) => ({
                            ...s,
                            environment: e.target.value,
                          }))
                        }
                      >
                        <option value="production">production</option>
                        <option value="staging">staging</option>
                        <option value="development">development</option>
                      </Select>
                    </GridItem>
                    <GridItem>
                      <Button
                        colorScheme="blue"
                        isLoading={createInstallationMutation.isPending}
                        isDisabled={!installationForm.installation_id.trim()}
                        onClick={() =>
                          createInstallationMutation.mutate({
                            licenseId: selectedLicenseId,
                            payload: {
                              installation_id:
                                installationForm.installation_id.trim(),
                              environment: installationForm.environment,
                            },
                          })
                        }
                      >
                        Add Installation
                      </Button>
                    </GridItem>
                  </Grid>
                  <Alert
                    status={
                      installationForm.runtime_target === "hrm"
                        ? "info"
                        : "warning"
                    }
                    borderRadius="md"
                    mb={3}
                  >
                    <AlertIcon />
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold">
                        {installationForm.runtime_target === "hrm"
                          ? "Kontrakt runtime HRM"
                          : "Kontrakt runtime WMS"}
                      </Text>
                      <Text fontSize="sm">
                        Odbiorca:{" "}
                        <Box as="span" fontFamily="mono">
                          {installationForm.runtime_target === "hrm"
                            ? "dbg-one-hrm-runtime"
                            : "dbg-wms-runtime"}
                        </Box>
                      </Text>
                      <Text fontSize="sm">
                        Bootstrap używa poświadczeń instalacji i odświeżania
                        runtime JWT.
                        {installationForm.runtime_target === "hrm"
                          ? " Bootstrap HRM nie zawiera startowego klienta ani identyfikatorów licencji."
                          : " Bootstrap WMS używa odświeżania powiązanego z instalacją z lokalnym fallbackiem cache runtime."}
                      </Text>
                    </Box>
                  </Alert>
                  {issuedSecrets ? (
                    <Box mb={3} borderWidth="1px" borderRadius="md" p={3}>
                      <HStack justify="space-between" mb={2} flexWrap="wrap">
                        <Text fontWeight="semibold">
                          Wydane sekrety: {issuedSecrets.installationId}
                        </Text>
                        <Button
                          size="xs"
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              `LMS_RUNTIME_SHARED_KEY=${issuedSecrets.runtimeSharedKey}\nLMS_WEBHOOK_SHARED_KEY=${issuedSecrets.webhookSharedKey}`,
                            )
                            toast({
                              title: "Sekrety zostały skopiowane",
                              status: "success",
                              duration: 2000,
                            })
                          }}
                        >
                          Kopiuj
                        </Button>
                      </HStack>
                      <Text fontSize="xs" color={mutedTextColor} mb={2}>
                        Pokażemy je tylko raz. Zapisz je w konfiguracji
                        konkretnej instalacji produktu.
                      </Text>
                      <Textarea
                        readOnly
                        rows={4}
                        fontFamily="mono"
                        fontSize="xs"
                        value={`LMS_RUNTIME_SHARED_KEY=${issuedSecrets.runtimeSharedKey}\nLMS_WEBHOOK_SHARED_KEY=${issuedSecrets.webhookSharedKey}`}
                      />
                    </Box>
                  ) : null}
                  {bootstrapSnippet ? (
                    <Box mb={3} borderWidth="1px" borderRadius="md" p={3}>
                      <HStack justify="space-between" mb={2} flexWrap="wrap">
                        <Text fontWeight="semibold">
                          {selectedLicense?.product === "hrm"
                            ? "Konfiguracja bootstrap HRM"
                            : "Konfiguracja bootstrap WMS"}
                        </Text>
                        <Button
                          size="xs"
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              bootstrapSnippet,
                            )
                            toast({
                              title:
                                "Konfiguracja bootstrap została skopiowana",
                              status: "success",
                              duration: 2000,
                            })
                          }}
                        >
                          Kopiuj blok .env
                        </Button>
                      </HStack>
                      <Text fontSize="xs" color={mutedTextColor} mb={2}>
                        Gotowy blok dla klienckiego `backend/.env`, zgodny z
                        wybranym produktem.
                      </Text>
                      <Textarea
                        readOnly
                        rows={13}
                        fontFamily="mono"
                        fontSize="xs"
                        value={bootstrapSnippet}
                      />
                    </Box>
                  ) : null}
                  {installationsQuery.isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <TableContainer borderWidth="1px" borderRadius="md">
                      <Table size="sm">
                        <Thead bg={tableHeadBg}>
                          <Tr>
                            <Th>Instalacja</Th>
                            <Th>Produkt</Th>
                            <Th>Środowisko</Th>
                            <Th>Status</Th>
                            <Th>Ostatnie użycie</Th>
                            <Th>Obrócono</Th>
                            <Th />
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(installationsQuery.data?.items ?? []).length ===
                          0 ? (
                            <Tr>
                              <Td
                                colSpan={7}
                                textAlign="center"
                                py={5}
                                color={mutedTextColor}
                              >
                                No installations registered yet.
                              </Td>
                            </Tr>
                          ) : (
                            (installationsQuery.data?.items ?? []).map(
                              (item) => (
                                <Tr key={item.id}>
                                  <Td fontFamily="mono" fontSize="xs">
                                    {item.installation_id}
                                  </Td>
                                  <Td>
                                    <Badge
                                      colorScheme={
                                        item.product === "hrm" ? "cyan" : "blue"
                                      }
                                      variant="subtle"
                                    >
                                      {item.product}
                                    </Badge>
                                  </Td>
                                  <Td>{item.environment}</Td>
                                  <Td>
                                    <Badge
                                      colorScheme={getCredentialStatusColor(
                                        item.status,
                                      )}
                                    >
                                      {item.status}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    {item.last_used_at
                                      ? new Date(
                                          item.last_used_at,
                                        ).toLocaleString()
                                      : "-"}
                                  </Td>
                                  <Td>
                                    {item.rotated_at
                                      ? new Date(
                                          item.rotated_at,
                                        ).toLocaleString()
                                      : "-"}
                                  </Td>
                                  <Td>
                                    <HStack justify="end">
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        colorScheme="blue"
                                        isLoading={
                                          rotateInstallationMutation.isPending &&
                                          rotateInstallationMutation.variables
                                            ?.installationId ===
                                            item.installation_id
                                        }
                                        onClick={() =>
                                          rotateInstallationMutation.mutate({
                                            licenseId: selectedLicenseId,
                                            installationId:
                                              item.installation_id,
                                          })
                                        }
                                      >
                                        Rotate
                                      </Button>
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        colorScheme="red"
                                        isLoading={
                                          revokeInstallationMutation.isPending &&
                                          revokeInstallationMutation.variables
                                            ?.installationId ===
                                            item.installation_id
                                        }
                                        isDisabled={item.status === "revoked"}
                                        onClick={() =>
                                          revokeInstallationMutation.mutate({
                                            licenseId: selectedLicenseId,
                                            installationId:
                                              item.installation_id,
                                          })
                                        }
                                      >
                                        Revoke
                                      </Button>
                                    </HStack>
                                  </Td>
                                </Tr>
                              ),
                            )
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
                <Box mt={2}>
                  {issuedToken ? (
                    <Box mb={3} borderWidth="1px" borderRadius="md" p={3}>
                      <HStack justify="space-between" mb={2} flexWrap="wrap">
                        <Text fontWeight="semibold">Wystawione JWT</Text>
                        <HStack>
                          <Button
                            size="xs"
                            onClick={async () => {
                              await navigator.clipboard.writeText(
                                issuedToken.token,
                              )
                              toast({
                                title: "JWT zostało skopiowane",
                                status: "success",
                                duration: 2000,
                              })
                            }}
                          >
                            Copy
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              const blob = new Blob([issuedToken.token], {
                                type: "text/plain;charset=utf-8",
                              })
                              const url = URL.createObjectURL(blob)
                              const a = document.createElement("a")
                              a.href = url
                              a.download = `${selectedLicenseId || "license"}.jwt`
                              a.click()
                              URL.revokeObjectURL(url)
                            }}
                          >
                            Download .jwt
                          </Button>
                        </HStack>
                      </HStack>
                      <Text fontSize="xs" color={mutedTextColor} mb={2}>
                        kid: {issuedToken.kid} | jti: {issuedToken.jti} |
                        expires:{" "}
                        {new Date(issuedToken.expiresAt).toLocaleString()}
                      </Text>
                      <Textarea
                        value={issuedToken.token}
                        readOnly
                        rows={5}
                        fontFamily="mono"
                        fontSize="xs"
                      />
                    </Box>
                  ) : null}
                  <Text fontWeight="semibold" mb={2}>
                    Issued Token History
                  </Text>
                  {detailQuery.isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <TableContainer borderWidth="1px" borderRadius="md">
                      <Table size="sm">
                        <Thead bg={tableHeadBg}>
                          <Tr>
                            <Th>JTI</Th>
                            <Th>KID</Th>
                            <Th>ALG</Th>
                            <Th>Wystawiono</Th>
                            <Th>Wygasa</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(detailQuery.data?.issued_tokens ?? []).length ===
                          0 ? (
                            <Tr>
                              <Td
                                colSpan={5}
                                textAlign="center"
                                py={5}
                                color={mutedTextColor}
                              >
                                No issued tokens yet.
                              </Td>
                            </Tr>
                          ) : (
                            (detailQuery.data?.issued_tokens ?? []).map((t) => (
                              <Tr key={t.id}>
                                <Td fontFamily="mono" fontSize="xs">
                                  {t.jti}
                                </Td>
                                <Td>{t.kid}</Td>
                                <Td>{t.alg}</Td>
                                <Td>
                                  {new Date(t.issued_at).toLocaleString()}
                                </Td>
                                <Td>
                                  {new Date(t.expires_at).toLocaleString()}
                                </Td>
                              </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </VStack>
            )}
          </Box>
        ) : null}
        {revokeConfirm.isOpen ? (
          <Box
            borderWidth="1px"
            borderColor={dangerBorderColor}
            borderRadius="md"
            p={3}
          >
            <Text mb={2}>
              Confirm revoke for <b>{selectedLicenseId}</b>?
            </Text>
            <HStack>
              <Button
                size="sm"
                colorScheme="red"
                onClick={() => {
                  revokeConfirm.onClose()
                  revokeMutation.mutate(selectedLicenseId)
                }}
              >
                Confirm Revoke
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={revokeConfirm.onClose}
              >
                Cancel
              </Button>
            </HStack>
          </Box>
        ) : null}
      </VStack>
    </Container>
  )
}
